import { FC, ReactNode } from "react";

import { Box } from "@mui/material";
import { EVENT_REMINDER_MAX_OFFSET, ScheduleOffsetUnit } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { AutomatedCommunicationCard } from ".././components/AutomatedCommunicationCard";
import { SendAutomatedEmailTestButton } from "../components/SendAutomatedEmailTestButton";
import { buildEventReminderChips } from "../helpers/buildEventReminderChips";
import { AutomatedCommunicationsFormData, RowKey } from "../types";

import { Translate, useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";

type EventReminder =
  | {
      state?: string;
      emailDeliveryId?: string | null;
    }
  | null
  | undefined;

interface EventReminderCardProps {
  num: 1 | 2;
  openRow: RowKey | null;
  onToggleOpen: (row: RowKey) => void;
  reminder: EventReminder;
  isMultiDate: boolean;
  isRemindersLoaded: boolean;
  bodyCustomConfig: { date: boolean; location: boolean; eTicket: boolean };
  defaultContent: string;
  onViewResults: (params: { emailDeliveryId: string | null }) => void;
}

export const EventReminderCard: FC<EventReminderCardProps> = ({
  num,
  openRow,
  onToggleOpen,
  reminder,
  isMultiDate,
  isRemindersLoaded,
  bodyCustomConfig,
  defaultContent,
  onViewResults,
}) => {
  const { t } = useTranslate();
  const form = useFormContext<AutomatedCommunicationsFormData>();

  const fieldKey = num === 1 ? ("reminder1" as const) : ("reminder2" as const);
  const rowKey: RowKey = num === 1 ? "reminder-1" : "reminder-2";

  const enabled = useWatch({ control: form.control, name: `${fieldKey}.enabled` });
  const offset = useWatch({ control: form.control, name: `${fieldKey}.offset` });
  const offsetUnit = useWatch({ control: form.control, name: `${fieldKey}.offsetUnit` });

  const sentOverride = getSentOverride({
    reminder,
    rowKey,
    openRow,
    onToggleOpen,
    isMultiDate,
    onViewResults,
    t,
  });

  return (
    <AutomatedCommunicationCard<AutomatedCommunicationsFormData>
      label={t("dashboard", `ticketing.setThankYouEmail.reminder${num}.label`)}
      control={form.control}
      errors={form.formState.errors}
      dataEmailType={rowKey}
      fields={{ subject: `${fieldKey}.subject`, content: `${fieldKey}.content` }}
      enabled={Boolean(enabled)}
      onEnabledChange={(next) => form.setValue(`${fieldKey}.enabled`, next, { shouldDirty: true })}
      chips={buildEventReminderChips({ t, offset, offsetUnit, state: reminder?.state })}
      collapse={sentOverride.collapseOverride ?? { isOpen: openRow === rowKey, onToggle: () => onToggleOpen(rowKey) }}
      hideToggle={sentOverride.hideToggle}
      disabledFieldsTooltip={sentOverride.disabledFieldsTooltip}
      additionalActions={sentOverride.additionalActions}
      offset={{
        name: `${fieldKey}.offset`,
        suffixLabel: t("dashboard", "ticketing.setThankYouEmail.reminder.beforeEvent"),
        unitField: {
          name: `${fieldKey}.offsetUnit`,
          options: [
            { value: ScheduleOffsetUnit.Days, label: t("dashboard", "ticketing.setThankYouEmail.reminder.unit.days") },
            {
              value: ScheduleOffsetUnit.Hours,
              label: t("dashboard", "ticketing.setThankYouEmail.reminder.unit.hours"),
            },
          ],
        },
        maxOffset: EVENT_REMINDER_MAX_OFFSET,
      }}
      body={{
        defaultValue: defaultContent,
        attachmentFieldName: `${fieldKey}.attachment`,
        customConfig: bodyCustomConfig,
        readyToMount: isRemindersLoaded,
      }}
      dataTestPrefix={`live-form-editor-reminder-${num}`}
      footnote={
        <Box marginTop={2} display="flex" justifyContent="flex-start">
          <SendAutomatedEmailTestButton<AutomatedCommunicationsFormData>
            emailType="ticketing-reminder"
            subjectFieldName={`${fieldKey}.subject`}
            contentFieldName={`${fieldKey}.content`}
            attachmentFieldName={`${fieldKey}.attachment`}
          />
        </Box>
      }
    />
  );
};

type ReminderSentOverride = {
  hideToggle?: boolean;
  disabledFieldsTooltip?: string;
  additionalActions?: ReactNode;
  collapseOverride?: { isOpen: boolean; onToggle: () => void };
};

const getSentOverride = ({
  reminder,
  rowKey,
  openRow,
  onToggleOpen,
  isMultiDate,
  onViewResults,
  t,
}: {
  reminder: EventReminder;
  rowKey: RowKey;
  openRow: RowKey | null;
  onToggleOpen: (row: RowKey) => void;
  isMultiDate: boolean;
  onViewResults: (params: { emailDeliveryId: string | null }) => void;
  t: Translate;
}): ReminderSentOverride => {
  const state = reminder?.state;
  if (state !== "sent_no_future" && state !== "sent_and_upcoming") {
    return {};
  }
  const emailDeliveryId = reminder?.emailDeliveryId ?? null;
  const viewResultsButton = (
    <DashboardButton
      vibe="neutral"
      variant="outlined"
      size="small"
      onClick={() => onViewResults({ emailDeliveryId })}
      data-test={`${rowKey}-view-results`}
    >
      {t("dashboard", "ticketing.setThankYouEmail.reminder.viewResults")}
    </DashboardButton>
  );
  if (state === "sent_and_upcoming") {
    return { additionalActions: viewResultsButton };
  }
  if (isMultiDate) {
    return {
      disabledFieldsTooltip: t("dashboard", "ticketing.setThankYouEmail.reminder.alreadySentTooltip"),
      additionalActions: viewResultsButton,
    };
  }
  return {
    hideToggle: true,
    disabledFieldsTooltip: t("dashboard", "ticketing.setThankYouEmail.reminder.alreadySentTooltip"),
    additionalActions: viewResultsButton,
    collapseOverride: { isOpen: openRow === rowKey, onToggle: () => onToggleOpen(rowKey) },
  };
};
